import { useEffect } from 'react'

import { Button, LoadingSection } from 'app/components/design-system-components'
import { keySetTypesOptions } from 'app/constants/key-logger.constants'
import { extractBorrowingInfo } from 'app/features/teams/key-logger/CheckInKeySetModal/utils'
import {
  SuccessModal,
  SuccessModalSimpleDescriptionList,
} from 'app/features/teams/key-logger/components/SuccessModal'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import {
  generateKeySetPopulateParams,
  getKeySet,
} from 'app/services/http/teams/key-logger'
import { useStateWithLoading } from 'app/utils/hooks/useStateWithLoading'

export const CheckoutSuccessModal = ({
  teamId,
  keySet: initialKeySet,
  bulkKeySets,
  closeModal,
}) => {
  const generateListDataForSingleKeySet = (keySet) => {
    const { property } = keySet
    const borrowingInfo = extractBorrowingInfo(keySet.borrowing_data)
    return [
      { title: 'Property Address', description: property.address.friendlyName },
      { title: 'Key set', description: keySet.label },
      {
        title: 'Key set type',
        description: keySetTypesOptions[keySet.set_type],
      },
      { title: 'Borrower', description: borrowingInfo?.borrower || 'N/A' },
      {
        title: 'Due date',
        description: borrowingInfo?.dueDate || 'N/A',
      },
    ]
  }

  const generateListDataForBulkKeySets = (keySets) => {
    const firstKeySet = keySets[0]
    const borrowingInfo = extractBorrowingInfo(firstKeySet.borrowing_data)
    return [
      { title: 'Number of key sets', description: keySets.length.toString() },
      { title: 'Borrower', description: borrowingInfo?.borrower || 'N/A' },
      {
        title: 'Due date',
        description: borrowingInfo?.dueDate || 'N/A',
      },
    ]
  }

  const {
    state: keySetInfo,
    setState: setKeySetInfo,
    loadingStates: keySetInfoLoadingStates,
    loadingStatesHelpers: keySetInfoLoadingStatesHelpers,
  } = useStateWithLoading({ keySet: null, keySets: null, listData: null })

  const loadKeySetData = () => {
    if (bulkKeySets && bulkKeySets.length > 0) {
      // For bulk checkout, we'll load all the key sets
      const keySetPromises = bulkKeySets.map((keySetGuid) =>
        getKeySet(
          teamId,
          keySetGuid,
          generateKeySetPopulateParams({
            property: true,
            borrower: true,
            issuer: true,
          }),
        ),
      )

      return Promise.all(keySetPromises)
        .then((responses) => {
          const keySets = responses.map((response) => response.key_set)
          setKeySetInfo({
            keySets,
            listData: generateListDataForBulkKeySets(keySets),
          })
          keySetInfoLoadingStatesHelpers.markDoneSuccessfully()
        })
        .catch((err) => keySetInfoLoadingStatesHelpers.setError(err))
    } else if (initialKeySet) {
      // For single key set checkout
      return getKeySet(
        teamId,
        initialKeySet.guid || initialKeySet,
        generateKeySetPopulateParams({
          property: true,
          borrower: true,
          issuer: true,
        }),
      )
        .then(({ key_set: keySet }) => {
          setKeySetInfo({
            keySet,
            listData: generateListDataForSingleKeySet(keySet),
          })
          keySetInfoLoadingStatesHelpers.markDoneSuccessfully()
        })
        .catch((err) => keySetInfoLoadingStatesHelpers.setError(err))
    }
  }

  useEffect(() => {
    loadKeySetData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isBulkCheckout = bulkKeySets && bulkKeySets.length > 0
  const title = isBulkCheckout ? 'Keys Checked Out' : 'Key Checked Out'
  const description = isBulkCheckout
    ? 'Multiple keys checked out successfully:'
    : 'Checked out info:'

  const actions = (
    <Button
      type="button"
      onClick={closeModal}
      variant="solid"
      width="100%"
      mb={theme.space[4] + 'px'}
    >
      Close
    </Button>
  )

  return (
    <SuccessModal title={title} description={description} actions={actions}>
      <LoadingSection
        height="100%"
        loadingState={keySetInfoLoadingStates}
        actionHandler={loadKeySetData}
        loaderProps={{ fontSize: theme.fontSizes.pExtraLarge18 }}
      >
        {(keySetInfo.keySet || keySetInfo.keySets) && (
          <SuccessModalSimpleDescriptionList
            listConfig={{ listData: keySetInfo.listData }}
          />
        )}
      </LoadingSection>
    </SuccessModal>
  )
}
